import IconRound from 'assets/logos/icon-round.svg?react';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import { cn } from 'lib/utils';
import * as React from 'react';

export type ChatItemProps = {
  avatar?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  dateString?: string;
  unread?: number;
  onClick?: () => void;
  className?: string;
};

/**
 * One conversation of the list (what react-chat-elements' `ChatItem` was):
 * avatar, name, last message, when, and the unread count. `data-slot`s are
 * the handles of the functional test suite.
 */
export default function ChatItem({
  avatar,
  title,
  subtitle,
  dateString,
  unread = 0,
  onClick,
  className,
}: ChatItemProps) {
  return (
    <button
      type="button"
      data-slot="chat-item"
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 border-b px-4 py-3 text-left last:border-b-0 hover:bg-accent/50',
        className,
      )}
      onClick={onClick}
    >
      <span data-slot="chat-item-avatar" className="shrink-0">
        <Avatar size="lg">
          <AvatarImage src={avatar} />
          <AvatarFallback className="bg-transparent text-inverse">
            <IconRound fill="currentColor" className="size-full" />
          </AvatarFallback>
        </Avatar>
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline gap-2">
          <span data-slot="chat-item-title" className="min-w-0 flex-1 truncate text-base font-medium text-foreground">
            {title}
          </span>
          {dateString && <span className="shrink-0 text-xs text-muted-foreground">{dateString}</span>}
        </span>
        <span className="flex items-center gap-2">
          <span
            data-slot="chat-item-subtitle"
            className="flex min-w-0 flex-1 items-center gap-1 text-sm text-muted-foreground"
          >
            {subtitle}
          </span>
          {unread > 0 && (
            <Badge data-slot="chat-item-unread" variant="inverse">
              {unread}
            </Badge>
          )}
        </span>
      </span>
    </button>
  );
}
